import {
  ArrowUpDown,
  Ban,
  Eye,
  Info,
  Pencil,
  Plus,
  Ruler,
  Search,
  Download,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { exportarCsv } from '@/lib/export';
import { cn, formatDateValue, formatDateTimeValue, formatNumber, toNumber } from '@/lib/utils';

import { RicNav } from '../components/ric-nav';
import {
  AnularTomaDialog,
  DetalleTomaDialog,
  EditarTomaDialog,
  TomaDialog,
} from '../components/tomas-dialogos';
import { useRicAuxiliares, useRicReporte, useRicUltimaToma } from '../hooks/use-ric';
import { estaAnulada, fondoFila } from '../lib/ric-format';
import type { RicFiltros, TomaRic } from '../ric.types';

/** Columnas del listado de tomas (una fila por toma). */
const COLUMNAS =
  'grid grid-cols-[3rem_2.5rem_7rem_6rem_6rem_6rem_6.5rem_6.5rem_6.5rem_6.5rem_6rem_8rem] items-center gap-x-2';

/** Primer día del mes en curso y hoy (valores sugeridos de la consulta). */
function rangoInicial(): { desde: string; hasta: string } {
  const hoy = new Date();
  const dosDigitos = (valor: number) => String(valor).padStart(2, '0');
  const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  const fin = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());

  const formatear = (fecha: Date) =>
    `${fecha.getFullYear()}-${dosDigitos(fecha.getMonth() + 1)}-${dosDigitos(fecha.getDate())}`;

  return { desde: formatear(inicio), hasta: formatear(fin) };
}

/**
 * RIC — Medidas (`/monitoreo/ric`).
 *
 * Puerto de `Reportes::ric` + `API/Tanks`: consulta de tomas por tanque y rango,
 * pestañas de activos y anulados, captura de la toma física, edición y anulación
 * con motivo obligatorio, y el detalle del cálculo.
 */
export function RicMedidasPage() {
  useDocumentTitle('RIC · Medidas');

  const [filtros, setFiltros] = useState<RicFiltros>(() => ({
    tank: 1,
    ...(() => {
      const rango = rangoInicial();

      return { fecha_inicio: rango.desde, fecha_fin: rango.hasta };
    })(),
  }));
  const [tab, setTab] = useState<'activos' | 'anulados'>('activos');
  const [capturando, setCapturando] = useState(false);
  const [editando, setEditando] = useState<TomaRic | null>(null);
  const [anulando, setAnulando] = useState<TomaRic | null>(null);
  const [detalle, setDetalle] = useState<TomaRic | null>(null);
  const [aviso, setAviso] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

  const auxiliares = useRicAuxiliares();
  const reporte = useRicReporte(filtros);
  const ultima = useRicUltimaToma(filtros.tank);

  const umbral = reporte.data?.threshold ?? auxiliares.data?.threshold ?? 100;
  const tanques = auxiliares.data?.tanks ?? [];

  /** El backend devuelve activos y anulados: aquí sólo se separan por pestaña. */
  const tomas = useMemo(() => reporte.data?.records ?? [], [reporte.data]);
  const activos = useMemo(() => tomas.filter((toma) => !estaAnulada(toma)), [tomas]);
  const anulados = useMemo(() => tomas.filter((toma) => estaAnulada(toma)), [tomas]);

  /** Orden descendente por fecha: lo último medido primero (como el panel). */
  const visibles = useMemo(() => {
    const lista = tab === 'activos' ? activos : anulados;

    return [...lista].sort((a, b) => (a.ric_date < b.ric_date ? 1 : -1));
  }, [tab, activos, anulados]);

  const nombreTanque = (id: number) => {
    const tanque = tanques.find((opcion) => opcion.id_tank === id);

    return tanque ? `Tanque ${tanque.id_tank} · ${tanque.producto ?? 'sin producto'}` : `Tanque ${id}`;
  };

  const exportar = () => {
    exportarCsv(
      `ric-tanque-${filtros.tank}`,
      [
        'Toma',
        'Tanque',
        'Fecha',
        'Existencia previa',
        'Ingresos',
        'Salidas',
        'Teórica',
        'Varillaje',
        'Físico',
        'Agua',
        'Retornos',
        'Diferencia',
        'Recibido',
        'Estado',
        'Registrado por',
        'Comentario',
        'Motivo anulación',
      ],
      visibles.map((toma) => [
        toma.ric_id,
        toma.ric_tank,
        toma.ric_date,
        toma.ric_previous.toFixed(3),
        toma.ric_incomes.toFixed(3),
        toma.ric_outcomes.toFixed(3),
        toma.ric_logical.toFixed(3),
        toma.ric_dipstick.toFixed(2),
        toma.ric_physical.toFixed(3),
        toma.ric_water_volume.toFixed(3),
        toma.ric_returns.toFixed(3),
        toma.ric_difference.toFixed(3),
        toma.ric_received_tanks.toFixed(3),
        estaAnulada(toma) ? 'Anulada' : 'Activa',
        toma.executor_nombre ?? '',
        toma.ric_comments ?? '',
        toma.ric_cancellation_reason ?? '',
      ]),
    );
  };

  const diferenciaAcumulada = activos.reduce((total, toma) => total + toNumber(toma.ric_difference), 0);
  const fueraDeUmbral = activos.filter(
    (toma) => Math.abs(toNumber(toma.ric_difference)) > umbral,
  ).length;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Ruler className="size-6 text-brand-500" aria-hidden="true" />
            RIC · Medidas
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Tomas físicas de inventario por tanque, con la diferencia entre lo teórico y lo medido.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <RicNav active="medidas" />
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Download className="size-3.5" aria-hidden="true" />}
            onClick={exportar}
            disabled={visibles.length === 0}
          >
            Exportar
          </Button>
          <Button
            size="sm"
            leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
            onClick={() => setCapturando(true)}
          >
            Capturar toma
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert
          variant={aviso.tipo === 'success' ? 'success' : 'error'}
          title={aviso.tipo === 'success' ? 'Listo' : 'Revisa el dato'}
        >
          {aviso.texto}
        </Alert>
      ) : null}

      <SectionCard
        title="Consulta"
        description="El rango se filtra por la fecha de cierre de la toma (día operativo anterior)"
      >
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex min-w-[13rem] flex-col gap-1 text-[11px] font-medium text-ink-500">
            Tanque
            <Select
              value={String(filtros.tank)}
              onChange={(event) => setFiltros({ ...filtros, tank: Number(event.target.value) })}
              className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            >
              {tanques.map((tanque) => (
                <option key={tanque.id_tank} value={String(tanque.id_tank)}>
                  Tanque {tanque.id_tank} · {tanque.producto ?? 'sin producto'}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
            Fecha inicio
            <CampoFecha
             
              value={filtros.fecha_inicio}
              max={filtros.fecha_fin || undefined}
              onChange={(event) => setFiltros({ ...filtros, fecha_inicio: event.target.value })}
              className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
             tipo="date" />
          </label>

          <label className="flex flex-col gap-1 text-[11px] font-medium text-ink-500">
            Fecha fin
            <CampoFecha
             
              value={filtros.fecha_fin}
              min={filtros.fecha_inicio || undefined}
              onChange={(event) => setFiltros({ ...filtros, fecha_fin: event.target.value })}
              className="h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
             tipo="date" />
          </label>

          <Button
            size="sm"
            variant="outline"
            leftIcon={<Search className="size-3.5" aria-hidden="true" />}
            onClick={() => void reporte.refetch()}
            loading={reporte.isRefetching}
          >
            Consultar
          </Button>

          <label className="flex items-center gap-2 pb-2.5 text-xs text-ink-600">
            <input
              type="checkbox"
              checked={filtros.incluir_anulados !== false}
              onChange={(event) =>
                setFiltros({ ...filtros, incluir_anulados: event.target.checked })
              }
              className="size-4 rounded border-ink-300"
            />
            Incluir anulados
          </label>
        </div>
      </SectionCard>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            Tomas activas
          </p>
          <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
            {formatNumber(activos.length)}
          </p>
          <p className="mt-0.5 text-xs text-ink-500">{anulados.length} anuladas</p>
        </div>

        <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            Diferencia acumulada
          </p>
          <p
            className={cn(
              'mt-1 font-mono text-lg font-bold tabular-nums',
              Math.abs(diferenciaAcumulada) > umbral ? 'text-rose-600' : 'text-ink-900',
            )}
          >
            {formatNumber(diferenciaAcumulada)} gal
          </p>
          <p className="mt-0.5 text-xs text-ink-500">Umbral configurado: {formatNumber(umbral)} gal</p>
        </div>

        <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            Fuera de umbral
          </p>
          <p
            className={cn(
              'mt-1 font-mono text-lg font-bold tabular-nums',
              fueraDeUmbral > 0 ? 'text-amber-600' : 'text-ink-900',
            )}
          >
            {formatNumber(fueraDeUmbral)}
          </p>
          <p className="mt-0.5 text-xs text-ink-500">
            {fueraDeUmbral > 0 ? 'Revisa esas tomas' : 'Todas dentro del umbral'}
          </p>
        </div>

        <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            Última toma activa
          </p>
          <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
            {activos[0] ? formatDateValue(activos[0].ric_date) : '—'}
          </p>
          <p className="mt-0.5 text-xs text-ink-500">
            {activos[0]
              ? `${formatNumber(activos[0].ric_physical)} gal · por ${activos[0].executor_nombre ?? 'sin usuario'}`
              : 'Sin tomas en el rango'}
          </p>
        </div>
      </div>

      <SectionCard
        title="Tomas del tanque"
        description={nombreTanque(filtros.tank)}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setTab('activos')}
              className={cn(
                'rounded-control px-3 py-1.5 text-xs font-semibold transition-colors',
                tab === 'activos'
                  ? 'bg-brand-600 text-white'
                  : 'border border-ink-200 bg-white text-ink-700',
              )}
            >
              Activos ({activos.length})
            </button>
            <button
              type="button"
              onClick={() => setTab('anulados')}
              className={cn(
                'rounded-control px-3 py-1.5 text-xs font-semibold transition-colors',
                tab === 'anulados'
                  ? 'bg-brand-600 text-white'
                  : 'border border-ink-200 bg-white text-ink-700',
              )}
            >
              Anulados ({anulados.length})
            </button>
          </div>
        }
      >
        {reporte.isPending ? (
          <Skeleton className="h-64 rounded-card" />
        ) : reporte.isError ? (
          <Alert variant="error" title="No se pudo cargar el RIC">
            <div className="flex flex-wrap items-center gap-3">
              <span>Verifica el rango de fechas e inténtalo nuevamente.</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void reporte.refetch()}
                loading={reporte.isRefetching}
              >
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : visibles.length === 0 ? (
          <div className="rounded-control border border-dashed border-ink-200 px-4 py-12 text-center">
            <Ruler className="mx-auto size-5 text-ink-300" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-ink-700">
              {tab === 'activos' ? 'Sin tomas activas' : 'Sin tomas anuladas'}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              Prueba con otro rango de fechas o captura una toma física nueva.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[78rem]">
              <div
                className={cn(
                  COLUMNAS,
                  'border-b border-ink-100 pb-2 text-[10px] font-semibold tracking-wider text-ink-400 uppercase',
                )}
              >
                <span>Toma</span>
                <span>Tanq.</span>
                <span>Fecha</span>
                <span className="text-right">Previo</span>
                <span className="text-right">Ingresos</span>
                <span className="text-right">Salidas</span>
                <span className="text-right">Teórica</span>
                <span className="text-right">Físico</span>
                <span className="text-right">Agua</span>
                <span className="text-right">Retornos</span>
                <span className="text-right">Diferencia</span>
                <span className="text-right">Acciones</span>
              </div>

              <ul className="mt-1">
                {visibles.map((toma) => (
                  <li
                    key={toma.ric_id}
                    className={cn(
                      COLUMNAS,
                      'border-b border-ink-100 py-2 last:border-b-0',
                      !estaAnulada(toma) && fondoFila(toma.ric_difference, umbral),
                    )}
                  >
                    <span className="flex items-center gap-1 font-mono text-xs font-semibold text-ink-800 tabular-nums">
                      #{toma.ric_id}
                      {estaAnulada(toma) ? (
                        <Ban className="size-3 text-rose-500" aria-hidden="true" />
                      ) : null}
                    </span>

                    <span className="font-mono text-xs text-ink-500 tabular-nums">
                      {toma.ric_tank}
                    </span>

                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-xs text-ink-700">
                        {formatDateTimeValue(toma.ric_date)}
                      </span>
                      <span className="truncate text-[10px] text-ink-400">
                        {toma.ric_comments ?? 'Sin comentario'}
                      </span>
                    </span>

                    <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                      {formatNumber(toma.ric_previous)}
                    </span>
                    <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                      {formatNumber(toma.ric_incomes)}
                    </span>
                    <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                      {formatNumber(toma.ric_outcomes)}
                    </span>
                    <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
                      {formatNumber(toma.ric_logical)}
                    </span>
                    <span className="text-right font-mono text-xs font-semibold text-ink-900 tabular-nums">
                      {formatNumber(toma.ric_physical)}
                    </span>
                    <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                      {formatNumber(toma.ric_water_volume)}
                    </span>
                    <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                      {formatNumber(toma.ric_returns)}
                    </span>
                    <span
                      className={cn(
                        'text-right font-mono text-xs tabular-nums',
                        Math.abs(toNumber(toma.ric_difference)) > umbral
                          ? 'font-semibold text-rose-600'
                          : 'text-ink-800',
                      )}
                    >
                      {formatNumber(toma.ric_difference)}
                    </span>

                    <span className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="Ver el cálculo"
                        aria-label={`Ver el cálculo de la toma ${toma.ric_id}`}
                        onClick={() => setDetalle(toma)}
                        className="flex size-8 items-center justify-center rounded-control text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
                      >
                        <Eye className="size-4" aria-hidden="true" />
                      </button>

                      {!estaAnulada(toma) ? (
                        <>
                          <button
                            type="button"
                            title="Editar toma"
                            aria-label={`Editar la toma ${toma.ric_id}`}
                            onClick={() => setEditando(toma)}
                            className="flex size-8 items-center justify-center rounded-control text-ink-400 transition-colors hover:bg-brand-50 hover:text-brand-700"
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            title="Anular toma"
                            aria-label={`Anular la toma ${toma.ric_id}`}
                            onClick={() => setAnulando(toma)}
                            className="flex size-8 items-center justify-center rounded-control text-ink-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                          >
                            <Ban className="size-4" aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <span className="flex size-8 items-center justify-center" title={toma.ric_cancellation_reason ?? ''}>
                          <Info className="size-4 text-ink-300" aria-hidden="true" />
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {tab === 'anulados' && visibles.length > 0 ? (
          <ul className="mt-4 space-y-2 border-t border-ink-100 pt-4">
            {visibles
              .filter((toma) => toma.ric_cancellation_reason)
              .slice(0, 5)
              .map((toma) => (
                <li key={`motivo-${toma.ric_id}`} className="text-xs text-ink-500">
                  <span className="font-mono font-semibold text-ink-700">#{toma.ric_id}</span>{' '}
                  {toma.ric_cancellation_reason} · {toma.cancelled_nombre ?? 'usuario'} (
                  {formatDateTimeValue(toma.ric_cancelled_at)})
                </li>
              ))}
          </ul>
        ) : null}

        {activos.some((toma) => toma.ric_edit_reason) ? (
          <div className="mt-4 border-t border-ink-100 pt-4">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              <ArrowUpDown className="size-3.5" aria-hidden="true" />
              Tomas editadas
            </p>
            <ul className="mt-2 space-y-1">
              {activos
                .filter((toma) => toma.ric_edit_reason)
                .slice(0, 5)
                .map((toma) => (
                  <li key={`edicion-${toma.ric_id}`} className="text-xs text-ink-500">
                    <span className="font-mono font-semibold text-ink-700">#{toma.ric_id}</span>{' '}
                    {toma.ric_edit_reason} · {toma.edited_nombre ?? 'usuario'} (
                    {formatDateTimeValue(toma.ric_edited_at)})
                  </li>
                ))}
            </ul>
          </div>
        ) : null}
      </SectionCard>

      {/* Los diálogos se montan sólo cuando hacen falta: así su estado interno
          se reinicia solo (sin efectos) cada vez que se abren. */}
      {capturando ? (
        <TomaDialog
          onClose={() => setCapturando(false)}
          tanques={tanques}
          tanqueInicial={filtros.tank}
          ultima={ultima.data}
          onCreada={(mensaje) => setAviso({ tipo: 'success', texto: mensaje })}
        />
      ) : null}

      {editando ? (
        <EditarTomaDialog
          key={editando.ric_id}
          toma={editando}
          onClose={() => setEditando(null)}
          onGuardado={(mensaje) => setAviso({ tipo: 'success', texto: mensaje })}
        />
      ) : null}

      {anulando ? (
        <AnularTomaDialog
          key={anulando.ric_id}
          toma={anulando}
          onClose={() => setAnulando(null)}
          onAnulada={(mensaje) => setAviso({ tipo: 'success', texto: mensaje })}
        />
      ) : null}

      {detalle ? (
        <DetalleTomaDialog toma={detalle} umbral={umbral} onClose={() => setDetalle(null)} />
      ) : null}
    </div>
  );
}
